'use client'

import { useIntl } from 'react-intl'
import { Bubble, Button, Spacer, Text } from '@vinted/web-ui'
import classNames from 'classnames'
import { useContext } from 'react'

import { UserPhoto } from '@marketplace-web/messaging/conversation-user-feature'
import {
  useConversationContext,
  ConversationListContext,
} from '@marketplace-web/messaging/conversation-providers-feature'
import {
  ConversationMessage,
  ApplicableTransactionAction,
  sellerActionOfferRequest,
} from '@marketplace-web/messaging/conversation-messaging-data'
import {
  OfferRequestStatus,
  acceptOffer,
  rejectOffer,
  OfferRequestMessage as OfferRequestMessageType,
  buyerBuyEvent,
  openOfferScreenEvent,
} from '@marketplace-web/offers/price-offers-data'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { useNavigateToCheckout } from '@marketplace-web/checkout/checkout-feature'
import {
  CheckoutOrderTypeMap,
  ExtraServiceOrderType,
} from '@marketplace-web/checkout/checkout-data'

import { formatCurrency } from '@marketplace-web/currency/currency-data'

import {
  MEMBER_PROFILE_URL,
  OFFER_REQUEST_ACCEPT_URL,
  OFFER_REQUEST_REJECT_URL,
} from '../../constants/routes'
import Action from './Action'
import { OfferRequestAction } from './types'
import MessageTime from '../MessageTime'
import { BuyButtonPosition } from '../constants'
import { isDateInTheFuture } from '../../utils/date'

type OwnProps = {
  isGrouped: boolean
  message: OfferRequestMessageType
  applicableTransactionActions?: Array<ApplicableTransactionAction>
  openOfferModal?: () => void
}

type Props = OwnProps & ConversationMessage

const OfferRequestMessage = ({
  isGrouped,
  message,
  createdTime,
  applicableTransactionActions,
  openOfferModal,
}: Props) => {
  const translate = useTranslate('conversation')
  const { track } = useTracking()

  const { navigateToCheckout } = useNavigateToCheckout()

  const { locale } = useIntl()
  const currentUserId = useSession().user?.id
  const { conversation, transaction, getConversationRequest } = useConversationContext()
  const { updateConversation } = useContext(ConversationListContext)
  const oppositeUser = conversation?.oppositeUser

  const buyNowButtonInConversation = useAbTest('dd_tx_web_buy_now_button_in_conversation')
  useTrackAbTest(buyNowButtonInConversation)

  const { statusTitle, status, price, originalPrice, currency } = message
  const isCurrentAuthor = message.userId === String(currentUserId)
  const arePendingOfferActionsVisible = status === OfferRequestStatus.Pending && !isCurrentAuthor
  const isOfferStatusVisible = status !== OfferRequestStatus.Pending || isCurrentAuthor
  const isCounterOfferActionVisible =
    status === OfferRequestStatus.Rejected &&
    !isCurrentAuthor &&
    message.isCurrentOffer &&
    applicableTransactionActions?.includes(ApplicableTransactionAction.Offer)

  const priceValue = typeof price === 'string' ? Number(price) : price.amount
  const originalPriceValue =
    typeof originalPrice === 'string' ? Number(originalPrice) : originalPrice.amount

  const messageClass = classNames('conversation-message', {
    'conversation-message__current-author': isCurrentAuthor,
    'conversation-message__single-action': isCounterOfferActionVisible,
    'conversation-message__actions': arePendingOfferActionsVisible,
  })

  const handleActionClick =
    (apiCall: typeof acceptOffer | typeof rejectOffer, action: OfferRequestAction) => async () => {
      if (!conversation?.transactionId) return

      const { transactionId } = conversation

      track(
        sellerActionOfferRequest({
          action,
          transactionId,
          offerRequestId: message.offerRequestId,
          currentPrice: originalPriceValue.toString(),
          offeredPrice: priceValue.toString(),
        }),
      )

      await apiCall({
        transactionId,
        offerRequestId: Number(message.offerRequestId),
      })

      getConversationRequest({
        conversationId: conversation.id,
      })

      updateConversation({ id: conversation.id })
    }

  const handleOpenOfferModal = () => {
    openOfferModal?.()

    if (!transaction) return

    track(
      openOfferScreenEvent({
        isBuyer: false,
        currentPrice: Number(transaction.offer.price.amount),
        currencyCode: transaction.offer.price.currencyCode,
        openedFrom: 'message_make_offer',
        transactionId: transaction.id,
      }),
    )
  }

  const renderPrices = () => {
    const priceLabel = formatCurrency(priceValue, { currency, locale })
    const originalPriceLabel = formatCurrency(originalPriceValue, { currency, locale })

    return (
      <div>
        <Text
          as="h2"
          text={priceLabel}
          type={Text.Type.Title}
          testId="offer-request-current-price-label"
        />
        <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
        <Text
          as="h2"
          text={originalPriceLabel}
          type={Text.Type.Title}
          theme="muted"
          strikethrough
          testId="offer-request-original-price-label"
        />
      </div>
    )
  }

  const renderActionState = () => {
    if (isCounterOfferActionVisible) {
      return (
        <>
          <Spacer />
          <Button
            text={translate('offer_request.offer_your_price')}
            size={Button.Size.Medium}
            styling={Button.Styling.Filled}
            onClick={handleOpenOfferModal}
            testId="counter-offer-button"
          />
        </>
      )
    }

    if (!arePendingOfferActionsVisible || !conversation?.transactionId) return null

    return (
      <>
        <Spacer />
        <Action
          text={translate('offer_request.accept')}
          url={OFFER_REQUEST_ACCEPT_URL(conversation.transactionId, message.offerRequestId)}
          onClick={handleActionClick(acceptOffer, OfferRequestAction.Accept)}
          styling={Action.Styling.Filled}
          testId="offer-message-accept-button"
        />
        <Spacer />
        <div className="u-flexbox">
          <div className="u-half-width">
            <Action
              text={translate('offer_request.decline')}
              url={OFFER_REQUEST_REJECT_URL(conversation.transactionId, message.offerRequestId)}
              onClick={handleActionClick(rejectOffer, OfferRequestAction.Reject)}
              testId="offer-message-decline-button"
            />
          </div>
          <Spacer orientation={Spacer.Orientation.Vertical} />
          <div className="u-half-width">
            <Button
              text={translate('offer_request.offer_your_price')}
              size={Button.Size.Medium}
              onClick={handleOpenOfferModal}
              testId="counter-offer-button"
            />
          </div>
        </div>
      </>
    )
  }

  const handleBuyButtonClick = () => {
    if (!conversation?.transaction?.itemId) return
    if (!conversation?.transaction?.id) return

    track(
      buyerBuyEvent({
        transactionId: conversation.transaction.id,
        screen: 'message_reply',
        buttonPosition: BuyButtonPosition.ConversationMessageOfferRequest,
      }),
    )

    if (!conversation?.transactionId) return

    navigateToCheckout(
      conversation.transactionId,
      CheckoutOrderTypeMap[ExtraServiceOrderType.Transaction],
    )
  }

  const renderUserPhoto = () => {
    if (!oppositeUser) return null

    const { photo, id, isSystem } = oppositeUser

    return (
      <div className="u-ui-margin-right-regular u-inline-block u-float-left">
        <UserPhoto
          photo={photo}
          profileUrl={MEMBER_PROFILE_URL(id)}
          isSystem={isSystem}
          isCurrentAuthor={isCurrentAuthor}
        />
      </div>
    )
  }

  const renderCurrencyExchangeRateNote = () => {
    const { isPriceFixed } = message

    if (isPriceFixed) return null
    if (status !== OfferRequestStatus.Pending && status !== OfferRequestStatus.Accepted) return null

    return (
      <>
        <Text as="h4" type={Text.Type.Caption} text={translate('currency_exchange_rate_note')} />
        <Spacer />
      </>
    )
  }

  const renderStatus = () => {
    if (!isOfferStatusVisible) return null

    return (
      <Text as="span" type={Text.Type.UserInput} text={statusTitle} testId="offer-status-title" />
    )
  }

  const isBuyButtonShown = () => {
    const showPaymentsButtons = conversation?.isPaymentActionAvailable
    const isBuyer = conversation?.buyerId === currentUserId
    const isOfferAccepted = status === OfferRequestStatus.Accepted

    const isSellerCountdownActive = isDateInTheFuture(transaction?.seller?.closetCountdownEndDate)

    return (
      message.isCurrentOffer &&
      showPaymentsButtons &&
      isBuyer &&
      isOfferAccepted &&
      !isSellerCountdownActive
    )
  }

  const renderBuyButton = () => {
    if (!buyNowButtonInConversation || buyNowButtonInConversation?.variant === 'off') return null
    if (!conversation?.transactionId || isBuyButtonShown() === false) return null

    return (
      <>
        <Spacer />
        <Button
          text={translate('offer_message.buy')}
          size={Button.Size.Medium}
          styling={Button.Styling.Filled}
          onClick={handleBuyButtonClick}
          testId="offer-request-message-buy-button"
        />
      </>
    )
  }

  return (
    <>
      {!isGrouped && <MessageTime time={createdTime} />}
      <div className="conversation-message-container u-ui-margin-top-medium u-fill-width u-inline-block">
        {renderUserPhoto()}
        <div className={messageClass}>
          <Bubble inverse={isCurrentAuthor}>
            {renderCurrencyExchangeRateNote()}
            {renderPrices()}
            {renderStatus()}
            {renderActionState()}
            {renderBuyButton()}
          </Bubble>
        </div>
      </div>
    </>
  )
}

export default OfferRequestMessage
